/**
 * source-detect.ts — Last-input-per-player tracker with transition subscription.
 *
 * Design decisions:
 *
 * 1. **First activity fires listeners** (`none → keyboard|gamepad` is a transition).
 *    The initial state is `'none'`; when the first activity is recorded for a
 *    player, `wasSource !== source` evaluates to `true` and all subscribed
 *    listeners are invoked. This is the most symmetric interpretation of
 *    "fires on transitions" — `none` is just another state.
 *
 * 2. **Older or equal timestamps are silently ignored** (newest activity wins).
 *    `if (timestampMs <= state.lastTs) return` handles both stale out-of-order
 *    events and duplicate timestamps at the same millisecond. No spurious
 *    flicker from equal-time events of different types.
 *
 * 3. **Set-based listener storage** — deduplicates by function reference.
 *    If the same function reference is passed to `subscribe` twice, it is
 *    stored once and will fire once per transition. Calling the returned
 *    unsubscribe removes that single entry. Users who need truly independent
 *    subscriptions for the same callback should wrap in distinct arrow
 *    functions or use `vi.fn()` (each call produces a unique reference).
 *
 * 4. **Unsubscribe is idempotent** — `Set.delete()` silently returns `false`
 *    if the listener was already removed. No throw on a second call.
 *
 * 5. **No global event bus** — each `SourceDetector` instance maintains its
 *    own `states` and `listeners` Maps. Multiple independent instances do not
 *    share state.
 */

import type { PlayerId } from './types';

/** The three possible source states for a player. */
export type InputSourceKind = 'keyboard' | 'gamepad' | 'none';

/** Listener signature for source-change events. */
export type SourceListener = (source: 'keyboard' | 'gamepad') => void;

/** Public interface for the input-source detector. */
export interface SourceDetector {
  /**
   * Record keyboard activity for a player at `timestampMs`.
   * If `timestampMs` is ≤ the player's last recorded timestamp, the call is
   * ignored (stale / equal-time event).
   */
  recordKeyboardActivity(playerId: PlayerId, timestampMs: number): void;

  /**
   * Record gamepad activity for a player at `timestampMs`.
   * Same timestamp-ordering rules as `recordKeyboardActivity`.
   */
  recordGamepadActivity(playerId: PlayerId, timestampMs: number): void;

  /**
   * Returns the most-recently-active input source for `playerId`.
   * Returns `'none'` if no activity has been recorded for that player.
   */
  getCurrentSource(playerId: PlayerId): InputSourceKind;

  /**
   * Subscribe to source-change events for `playerId`.
   *
   * The listener fires ONLY on transitions (e.g. `'keyboard' → 'gamepad'`).
   * The initial `'none' → <source>` when the first activity is recorded also
   * counts as a transition and fires the listener (design decision #1 above).
   *
   * Subscribing after activity has already been recorded does NOT immediately
   * fire the listener for the current state — only subsequent transitions do.
   *
   * Returns an unsubscribe function. Calling it removes the listener.
   * Calling unsubscribe more than once is idempotent (no throw).
   */
  subscribe(playerId: PlayerId, listener: SourceListener): () => void;
}

interface PlayerState {
  source: InputSourceKind;
  /** Monotonic watermark — events with `timestampMs <= lastTs` are ignored. */
  lastTs: number;
}

/**
 * Factory — creates a new, independent `SourceDetector` instance.
 * No shared global state; callers own the returned object's lifetime.
 */
export function createSourceDetector(): SourceDetector {
  const states = new Map<PlayerId, PlayerState>();
  const listeners = new Map<PlayerId, Set<SourceListener>>();

  function recordActivity(
    playerId: PlayerId,
    timestampMs: number,
    source: 'keyboard' | 'gamepad',
  ): void {
    const state: PlayerState = states.get(playerId) ?? { source: 'none', lastTs: -Infinity };

    // Ignore stale or equal-time events (design decision #2).
    if (timestampMs <= state.lastTs) return;

    const wasSource = state.source;
    state.source = source;
    state.lastTs = timestampMs;
    states.set(playerId, state);

    // Fire listeners only on transitions (including none → first source).
    if (wasSource !== source) {
      const playerListeners = listeners.get(playerId);
      if (playerListeners) {
        for (const cb of playerListeners) {
          cb(source);
        }
      }
    }
  }

  return {
    recordKeyboardActivity(playerId: PlayerId, timestampMs: number): void {
      recordActivity(playerId, timestampMs, 'keyboard');
    },

    recordGamepadActivity(playerId: PlayerId, timestampMs: number): void {
      recordActivity(playerId, timestampMs, 'gamepad');
    },

    getCurrentSource(playerId: PlayerId): InputSourceKind {
      return states.get(playerId)?.source ?? 'none';
    },

    subscribe(playerId: PlayerId, listener: SourceListener): () => void {
      let playerListeners = listeners.get(playerId);
      if (!playerListeners) {
        playerListeners = new Set<SourceListener>();
        listeners.set(playerId, playerListeners);
      }
      playerListeners.add(listener);

      return (): void => {
        // Idempotent: Set.delete returns false if not present — no throw.
        playerListeners!.delete(listener);
        if (playerListeners!.size === 0) {
          listeners.delete(playerId);
        }
      };
    },
  };
}
